import { Typography } from "@/frontend/shared/ui/Typography";
import styles from "./Comments.module.scss";
import { DotsThreeIcon, EditIcon, PlusIcon, TrashIcon } from "@/frontend/shared/assets/icons";
import { Textarea } from "@/frontend/shared/ui/Textarea/Textarea";
import { NormalButton, SquareButton } from "@/frontend/shared/ui/Button";
import { useMe } from "@/frontend/shared/lib/hooks/useMe";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { ConfirmationModal } from "@/frontend/shared/ui/ConfirmationModal/ConfirmationModal";
import { useState } from "react";
import { DropshadowMenu } from "@/frontend/shared/ui/DropshadowMenu";
import { getHowLongTimeString } from "@/frontend/shared/lib/getHowLongTimeString/getHowLongTimeString";
import { NoCommentsContent } from "./NoCommentsContent/NoCommentsContent";
import clsx from "clsx";
import { COMMENTS_BREAKPOINT } from "@/frontend/shared/const/breakpoints";

export type TCommentsProps = {
  value: string;
  setValue: (value: string) => void;
  postNote: () => void;
  data?: any;
  triggerRefetch: () => void;
  deleteFn: (noteId: number) => void;
  patchFn: (content: string, noteId: number) => void;
  showTitle?: boolean;
  className?: string;
};

export function Comments({
  value,
  setValue,
  postNote,
  data,
  triggerRefetch,
  showTitle = true,
  className,
  deleteFn,
  patchFn
}: TCommentsProps) {
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState({ isOpen: false, noteId: -1 });
  const [isDropshadowMenuOpen, setIsDropshadowMenuOpen] = useState({ isOpen: false, noteId: -1 });
  const [editingNote, setEditingNote] = useState({ noteId: -1 });
  const [editingValue, setEditingValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  function onClickCancel() {
    setIsEditing(false);
    setEditingValue("");
  }

  function onCloseModal() {
    setIsConfirmationModalOpen({ isOpen: false, noteId: 0 });
  }

  const { data: profileData } = useMe();

  async function deleteNote(noteId: number) {
    try {
      setIsLoading(true);
      await deleteFn(noteId);
      triggerRefetch();
      setIsConfirmationModalOpen({ isOpen: false, noteId: 0 });
      snackbarConstructor(`Note successfully deleted`, "normal");
      setIsLoading(false);
    } catch (error) {
      snackbarConstructor(`Failed to delete note`, "error");
      setIsLoading(false);
    }
  }

  function onClickEdit({ value, noteId }: { value: string; noteId: number }) {
    setEditingValue(value);
    setIsDropshadowMenuOpen({ isOpen: false, noteId: 0 });
    setIsEditing(true);
    setEditingNote({ noteId: noteId });
  }

  async function onClickPost({ value }: { value: string }) {
    if (value.length > 2200) {
      snackbarConstructor(`Your input exceeds the maximum allowed limit of 2200 characters.`, "error");
      return;
    }

    if (!isEditing) {
      postNote();
    } else {
      try {
        await patchFn(editingValue, editingNote.noteId);
        setIsEditing(false);
        triggerRefetch();
        snackbarConstructor(`Note successfully updated`, "normal");
        setEditingNote({ noteId: -1 });
        setEditingValue("");
      } catch (error) {
        snackbarConstructor(`Failed to update note`, "error");
      }
    }
  }

  const hasComments = data?.length > 0;

  return (
    <>
      <div className={clsx(styles.container, className)}>
        {showTitle ? (
          <div className={styles.titleContainer}>
            <Typography variant="heading-7" color="greyscale700">
              Comments
            </Typography>
          </div>
        ) : (
          <div />
        )}
        <div className={styles.contentContainer}>
          {hasComments ? (
            data?.map((item: any) => {
              const {
                createdBy: { firstName, lastName, profileImageUrl },
                createdAt,
                content,
                id
              } = item;

              switch (true) {
                case isEditing && editingNote.noteId === id:
                  return (
                    <div className={styles.editNoteContainer}>
                      <div className={styles.noteTitle}>
                        <div className={styles.infoSection}>
                          {profileImageUrl ? (
                            <img className={styles.image} src={profileImageUrl} alt="Profile" />
                          ) : (
                            <div className={styles.image}>
                              <Typography variant="label-5">
                                {firstName.charAt(0).toUpperCase() + lastName.charAt(0).toUpperCase()}
                              </Typography>
                            </div>
                          )}
                          <Typography variant="label-4" color="greyscale700">
                            {profileData?.firstName} {profileData?.lastName}
                          </Typography>
                        </div>
                      </div>
                      <div className={styles.editNoteContent}>
                        <Textarea
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          rows={1}
                          className={styles.textarea}
                          placeholder="Write a comment..."
                        />
                        <div className={styles.buttonsContainer}>
                          <NormalButton size="small" color="greyscale500" variant="secondary" onClick={onClickCancel}>
                            Cancel
                          </NormalButton>
                          <NormalButton size="small" onClick={() => onClickPost({ value })}>
                            Post
                          </NormalButton>
                        </div>
                      </div>
                    </div>
                  );

                default:
                  return (
                    <div className={styles.note} key={item.id}>
                      <DropshadowMenu
                        isOpen={isDropshadowMenuOpen.noteId === id}
                        right={"10px"}
                        top={"10px"}
                        width={"calc(100% - 80px)"}
                        className={styles.dropshadow}
                        options={[
                          <div>
                            <div
                              className={styles.dropshadowOption}
                              onClick={() => onClickEdit({ value: content, noteId: id })}
                            >
                              <EditIcon />
                              <Typography variant="label-4">Edit</Typography>
                            </div>
                            <div
                              className={styles.dropshadowOption}
                              onClick={() => setIsConfirmationModalOpen({ isOpen: true, noteId: id })}
                            >
                              <TrashIcon /> <Typography variant="label-4">Delete</Typography>
                            </div>
                          </div>
                        ]}
                        onClose={() => setIsDropshadowMenuOpen({ isOpen: false, noteId: -1 })}
                      />
                      <div className={styles.noteTitle}>
                        <div className={styles.infoSection}>
                          {profileImageUrl ? (
                            <img className={styles.image} src={profileImageUrl} alt="Profile" />
                          ) : (
                            <div className={styles.image}>
                              <Typography variant="label-5">
                                {firstName.charAt(0).toUpperCase() + lastName.charAt(0).toUpperCase()}
                              </Typography>
                            </div>
                          )}
                          <Typography variant="label-4" color="greyscale900">
                            {firstName} {lastName}
                          </Typography>
                          <Typography variant="body-2" color="greyscale500">
                            {getHowLongTimeString(new Date(createdAt))}
                          </Typography>
                        </div>
                        <DotsThreeIcon
                          className={styles.menuIcon}
                          onClick={() => setIsDropshadowMenuOpen({ isOpen: true, noteId: id })}
                        />
                      </div>
                      <div className={styles.noteContent}>
                        <Typography variant="body-2" color="greyscale700">
                          {content}
                        </Typography>
                      </div>
                    </div>
                  );
              }
            })
          ) : (
            <NoCommentsContent />
          )}
        </div>
        <div className={styles.newNoteContainer}>
          <Textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            rows={3}
            className={styles.textarea}
            placeholder="Write a comment..."
          />
          <SquareButton className={styles.button} onClick={() => onClickPost({ value })} isDisabled={!value.trim()}>
            <PlusIcon />
          </SquareButton>
        </div>
      </div>
      <ConfirmationModal
        onConfirm={() => deleteNote(isConfirmationModalOpen.noteId)}
        isDisabled={isConfirmationModalOpen.noteId === 0 || isLoading}
        isOpen={isConfirmationModalOpen.isOpen}
        onClose={onCloseModal}
        title="Delete Note"
        content="Are you sure you want to delete this note?"
        dangerConfirm
        mobileBreakpoint={COMMENTS_BREAKPOINT}
      />
    </>
  );
}
